import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { CheckIcon, PlusIcon, SearchIcon, ClockIcon, SendIcon, MicIcon, TagIcon, PlayIcon, PauseIcon, ChevronDownIcon, ChevronUpIcon, XIcon } from 'lucide-react';
import { mockData } from '../utils/mockData';
import { Button } from '../components/common/Button';
import { Dropdown } from '../components/common/Dropdown';
interface NoteCardProps {
  note: any;
  isSelected: boolean;
  isMultiSelectActive: boolean;
  onSelect: (id: string) => void;
  onDragStart: (e: React.DragEvent, id: string) => void;
  isPlaying: boolean;
  onTogglePlay: (e: React.MouseEvent, id: string) => void;
  onReassign: (noteId: string, incidentTitle: string) => void;
  onDropdownOpen?: (rect: DOMRect) => void;
}
const NoteCard = ({
  note,
  isSelected,
  isMultiSelectActive,
  onSelect,
  onDragStart,
  isPlaying,
  onTogglePlay,
  onReassign,
  onDropdownOpen
}: NoteCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  useLayoutEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const hasOverflow = textRef.current.scrollHeight > textRef.current.clientHeight;
        setIsOverflowing(hasOverflow);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [note.content]);
  const handleClick = () => {
    if (isMultiSelectActive) {
      onSelect(note.id);
    } else {
      alert(`Opening editor for note: ${note.id}`);
    }
  };
  return <div draggable={true} onDragStart={e => onDragStart(e, note.id)} onClick={handleClick} className={`group relative px-8 py-6 min-h-[118px] rounded-2xl border transition-all duration-200 cursor-move ${isMultiSelectActive && isSelected ? 'bg-[#2d4c6b] border-[#4a90e2] shadow-lg shadow-[#4a90e2]/10' : 'bg-[#151A22]/40 border-command-border hover:border-white/10 hover:bg-[#151A22]/60'}`} data-id="element-235">
      <div className="flex justify-between items-start mb-4" data-id="element-236">
        <div className="flex items-center gap-4" data-id="element-237">
          <div className="flex items-center gap-2" data-id="element-238">
            <span className="text-sm font-medium text-[#f59e0b]" data-id="element-239">
              {note.timestamp}
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-240">
            <span className="text-[10px] font-bold text-[#f59e0b] uppercase tracking-widest" data-id="element-241">
              {note.type === 'typed' ? 'MANUAL ENTRY' : 'TRANSCRIBED'}
            </span>
          </div>
          {!isMultiSelectActive && <Dropdown onOpen={onDropdownOpen} trigger={<div className={`flex items-center gap-2 px-3 py-1 bg-white/5 border border-command-border rounded-md text-[10px] font-bold uppercase tracking-wider hover:bg-white/10 transition-all cursor-pointer ${note.incident ? 'text-[#4a90e2]' : 'text-[var(--text-tertiary)]'}`} data-id="element-243">
                  <span data-id="element-244">{note.incident || '+ Add to Incident'}</span>
                  <ChevronDownIcon size={10} className="text-[var(--text-tertiary)]" data-id="element-245" />
                </div>} data-id="element-242">
              <div className="py-1" data-id="element-246">
                <div className="px-3 py-1.5 text-[10px] font-bold text-command-text-muted uppercase tracking-wider" data-id="element-247">
                  {note.incident ? 'Reassign Incident' : 'Assign to Incident'}
                </div>
                {[...mockData.sidebar.fieldNotes, ...mockData.sidebar.activeIncidents].map(inc => <button key={inc.id} onClick={e => {
              e.stopPropagation();
              onReassign(note.id, inc.title);
            }} className="w-full text-left px-3 py-2 text-xs text-[#e0e0e0] hover:bg-[#2a2a2a] transition-colors flex items-center gap-2" data-id="element-248">
                    <div className={`w-1.5 h-1.5 rounded-full ${inc.title === note.incident ? 'bg-[#4a90e2]' : 'bg-command-text-muted'}`} data-id="element-249" />
                    {inc.title}
                  </button>)}
              </div>
            </Dropdown>}
        </div>
        {note.type === 'transcript' && !isMultiSelectActive && <button onClick={e => {
        e.stopPropagation();
        onTogglePlay(e, note.id);
      }} className={`transition-all ${isPlaying ? 'text-[#4a90e2]' : 'text-[#444444] hover:text-[#e0e0e0]'}`} data-id="element-250">
            {isPlaying ? <PauseIcon size={20} fill="currentColor" data-id="element-251" /> : <PlayIcon size={20} fill="currentColor" data-id="element-252" />}
          </button>}
      </div>

      <div className="relative" data-id="element-253">
        <p ref={textRef} className={`text-lg text-[#e0e0e0] leading-relaxed font-medium ${!isExpanded ? 'line-clamp-1' : ''}`} data-id="element-254">
          {note.content}
        </p>

        {(isOverflowing || isExpanded) && <div className="flex items-center justify-end mt-4" data-id="element-255">
            <button onClick={e => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }} className="flex items-center gap-1 text-xs font-bold text-[#666666] hover:text-[#888888] transition-colors" data-id="element-256">
              {isExpanded ? <>
                  <span className="text-[var(--text-tertiary)]" data-id="element-257">Show Less</span>{' '}
                  <ChevronUpIcon size={14} className="text-[var(--text-tertiary)]" data-id="element-258" />
                </> : <>
                  <span className="text-[var(--text-tertiary)]" data-id="element-259">Show More</span>{' '}
                  <ChevronDownIcon size={14} className="text-[var(--text-tertiary)]" data-id="element-260" />
                </>}
            </button>
          </div>}
      </div>
    </div>;
};
export const FieldNotesFeed = () => {
  const [notes, setNotes] = useState(mockData.sidebar.fieldNotesFeed);
  const [selectedNotes, setSelectedNotes] = useState<string[]>([]);
  const [isMultiSelectActive, setIsMultiSelectActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isDeleteArmed, setIsDeleteArmed] = useState(false);
  const [playingNoteId, setPlayingNoteId] = useState<string | null>(null);
  const [transcribedText, setTranscribedText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const armTimerRef = useRef<NodeJS.Timeout | null>(null);
  const transcriptionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sampleTranscription = "Arriving on scene at the rear of the property. I'm observing fresh forced entry marks on the sliding glass door. There's a discarded pry bar in the grass near the patio. Proceeding with caution. Dispatch, be advised I'm entering the structure now.";
  useEffect(() => {
    if (isDeleteArmed) {
      armTimerRef.current = setTimeout(() => {
        setIsDeleteArmed(false);
      }, 5000);
    }
    return () => {
      if (armTimerRef.current) clearTimeout(armTimerRef.current);
    };
  }, [isDeleteArmed]);
  useEffect(() => {
    if (isVoiceMode) {
      setTranscribedText('');
      const words = sampleTranscription.split(' ');
      let currentWordIndex = 0;
      transcriptionIntervalRef.current = setInterval(() => {
        if (currentWordIndex < words.length) {
          setTranscribedText(prev => prev + (prev ? ' ' : '') + words[currentWordIndex]);
          currentWordIndex++;
        } else {
          if (transcriptionIntervalRef.current) clearInterval(transcriptionIntervalRef.current);
        }
      }, 100 + Math.random() * 100);
    } else {
      if (transcriptionIntervalRef.current) clearInterval(transcriptionIntervalRef.current);
    }
    return () => {
      if (transcriptionIntervalRef.current) clearInterval(transcriptionIntervalRef.current);
    };
  }, [isVoiceMode]);
  const handleDelete = () => {
    if (!isDeleteArmed) {
      setIsDeleteArmed(true);
    } else {
      alert(`Deleted ${selectedNotes.length} notes`);
      setSelectedNotes([]);
      setIsDeleteArmed(false);
    }
  };
  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  };
  useEffect(() => {
    autoResizeTextarea();
  }, [message]);
  useEffect(() => {
    // Small delay to ensure content is rendered
    const timer = setTimeout(() => {
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({
          behavior: 'auto'
        });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  const toggleNoteSelection = (id: string) => {
    setSelectedNotes(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  const togglePlay = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setPlayingNoteId(prev => prev === id ? null : id);
  };
  const handleDragStart = (e: React.DragEvent, noteId: string) => {
    e.stopPropagation();
    const draggedNotes = selectedNotes.includes(noteId) ? selectedNotes : [noteId];
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify(draggedNotes));
  };
  const handleReassign = (noteId: string, incidentTitle: string) => {
    setNotes(prev => prev.map(n => n.id === noteId ? {
      ...n,
      incident: incidentTitle
    } : n));
  };
  const filteredNotes = notes.filter(note => {
    const matchesSearch = note.content.toLowerCase().includes(searchQuery.toLowerCase());
    if (isMultiSelectActive) {
      return matchesSearch && !note.incident;
    }
    return matchesSearch;
  });
  const incidentCount = new Set(filteredNotes.map(n => n.incident).filter(Boolean)).size;
  const noteCount = filteredNotes.length;
  const handleDropdownOpen = (dropdownRect: DOMRect) => {
    if (!scrollContainerRef.current || !footerRef.current) return;
    const footerTop = footerRef.current.getBoundingClientRect().top;
    const dropdownBottom = dropdownRect.bottom + 200; // Estimate dropdown height
    if (dropdownBottom > footerTop) {
      const scrollAmount = dropdownBottom - footerTop + 20;
      scrollContainerRef.current.scrollBy({
        top: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  return <main className="flex-grow flex flex-col h-full min-h-0 overflow-hidden relative bg-[#0a1120]" data-id="element-261">
      {/* Transcription Surface Overlay */}
      {isVoiceMode && <div className="absolute inset-0 z-50 bg-command-app/95 backdrop-blur-xl flex items-center justify-center p-8 animate-fade-in" data-id="element-262">
          <div className="w-full max-w-[800px] bg-command-panel border border-command-border-soft rounded-3xl p-12 shadow-2xl relative animate-slide-up" data-id="element-263">
            <button onClick={() => setIsVoiceMode(false)} className="absolute top-6 right-6 p-2 hover:bg-command-input rounded-full text-command-text-muted hover:text-white transition-colors" data-id="element-264">
              <XIcon size={24} data-id="element-265" />
            </button>

            <div className="flex items-center gap-3 mb-8" data-id="element-266">
              <div className="w-3 h-3 rounded-full bg-[#ff4444] animate-pulse" data-id="element-267" />
              <span className="text-xs font-bold text-[#888888] uppercase tracking-[0.2em]" data-id="element-268">
                Live Transcription
              </span>
            </div>

            <div className="min-h-[200px]" data-id="element-269">
              <p className="text-3xl md:text-4xl font-medium text-white leading-tight" data-id="element-270">
                {transcribedText}
                <span className="inline-block w-1 h-8 bg-[#4a90e2] ml-2 animate-pulse align-middle" data-id="element-271" />
              </p>
            </div>

            <div className="mt-12 flex justify-center" data-id="element-272">
              <Button variant="primary" size="lg" onClick={() => {
            alert('Transcription saved to incident.');
            setIsVoiceMode(false);
          }} data-id="element-273">
                Save Note
              </Button>
            </div>
          </div>
        </div>}

      <div className="flex-grow flex flex-col min-h-0 overflow-hidden" data-id="element-274">
        {/* Sticky Header */}
        <div className="flex-shrink-0 p-8 border-b border-command-border bg-[#0a1120]/80 backdrop-blur-md z-20" data-id="element-275">
          <div className="max-w-[1000px] mx-auto" data-id="element-276">
            <div className="flex justify-between items-end" data-id="element-277">
              <div data-id="element-278">
                <div className="flex items-center gap-2 mb-2" data-id="element-279">
                  <div className="w-2 h-2 rounded-full bg-[#f59e0b]" data-id="element-280" />
                  <span className="text-[10px] font-bold text-[#f59e0b] uppercase tracking-[0.2em]" data-id="element-281">
                    {isMultiSelectActive ? `${noteCount} UNTAGGED NOTES READY FOR INCIDENT CREATION` : `${noteCount.toString().padStart(2, '0')} ACTIVE NOTES ACROSS ${incidentCount} INCIDENTS`}
                  </span>
                </div>
                <h1 className="text-3xl font-bold text-white tracking-tight" data-id="element-282">
                  Live Notes
                </h1>
              </div>
              <div className="flex items-center gap-4" data-id="element-283">
                {!isMultiSelectActive && <div className="relative w-64" data-id="element-284">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={16} data-id="element-285" />
                    <input type="text" placeholder="Search" className="w-full bg-[#151A22]/50 border border-command-border rounded-lg py-2 pl-10 pr-4 text-sm text-[#e0e0e0] focus:outline-none focus:border-white/10 transition-colors placeholder:text-[var(--placeholder)]" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-286" />
                  </div>}
                <div className="flex gap-3" data-id="element-287">
                  {!isMultiSelectActive ? <Button variant="secondary" icon={PlusIcon} onClick={() => setIsMultiSelectActive(true)} data-id="element-288">
                      Select Notes
                    </Button> : <div className="flex gap-3 animate-slide-up" data-id="element-289">
                      <Button variant="primary" disabled={selectedNotes.length === 0} onClick={() => alert(`Combining ${selectedNotes.length} notes into a new incident...`)} data-id="element-290">
                        Combine{' '}
                        {selectedNotes.length > 0 ? `(${selectedNotes.length})` : ''}
                      </Button>
                      <button onClick={handleDelete} disabled={selectedNotes.length === 0} className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${selectedNotes.length === 0 ? 'bg-command-input border border-command-border-soft text-command-text-muted opacity-50 cursor-not-allowed' : isDeleteArmed ? 'bg-[#ff4444] text-white shadow-lg shadow-red-500/20' : 'bg-command-input border border-command-border-soft text-command-text-muted hover:bg-command-panel hover:text-[#ff4444]'}`} data-id="element-291">
                        {isDeleteArmed ? 'Confirm Delete' : 'Delete'}
                        {isDeleteArmed && <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" data-id="element-292" />}
                      </button>
                      <Button variant="secondary" onClick={() => {
                    setIsMultiSelectActive(false);
                    setSelectedNotes([]);
                    setIsDeleteArmed(false);
                  }} data-id="element-293">
                        Cancel
                      </Button>
                    </div>}
                </div>
              </div>
            </div>
            {isMultiSelectActive && <div className="mt-6 animate-slide-down" data-id="element-294">
                <div className="relative w-full" data-id="element-295">
                  <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-command-text-muted" size={20} data-id="element-296" />
                  <input type="text" placeholder="Search untagged notes..." className="w-full bg-[#151A22]/50 border border-command-border rounded-xl py-3 pl-12 pr-4 text-[#e0e0e0] focus:outline-none focus:border-white/10 transition-colors text-lg placeholder:text-command-text-muted" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-297" />
                </div>
              </div>}
          </div>
        </div>

        {/* Scrollable Content */}
        <div ref={scrollContainerRef} className="flex-grow min-h-0 overflow-y-auto custom-scrollbar relative" data-id="element-298">
          <div className="p-8 pb-8 max-w-[1000px] mx-auto w-full flex flex-col" data-id="element-299">
            <div className="flex-grow" data-id="element-300">
              <div className="relative ml-8 pl-12 border-l border-[var(--timeline-line)] space-y-8 py-4" data-id="element-301">
                {filteredNotes.map(note => <div key={note.id} className="relative group/note" data-id="element-302">
                    {/* Timeline Dot - Hollow circle matching image */}
                    <div className={`absolute -left-[57px] top-7 w-4 h-4 rounded-full bg-[#0a1120] border-2 transition-all z-10 group-hover/note:scale-110 ${selectedNotes.includes(note.id) ? 'border-[var(--timeline-dot)] bg-[var(--timeline-dot)]' : 'border-[var(--timeline-dot)]'}`} data-id="element-303" />

                    <NoteCard note={note} isSelected={selectedNotes.includes(note.id)} isMultiSelectActive={isMultiSelectActive} onSelect={toggleNoteSelection} onDragStart={handleDragStart} isPlaying={playingNoteId === note.id} onTogglePlay={togglePlay} onReassign={handleReassign} onDropdownOpen={handleDropdownOpen} data-id="element-304" />
                  </div>)}
                <div ref={bottomRef} data-id="element-305" />
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Chat Footer */}
        <div ref={footerRef} className="flex-shrink-0 p-8 pt-4 border-t border-command-border bg-[#0a1120]/80 backdrop-blur-md z-30" data-id="element-306">
          <div className="max-w-[1000px] mx-auto flex items-center gap-4 h-16" data-id="element-307">
            <div className="flex-grow bg-[#151A22]/50 border border-command-border rounded-2xl px-5 flex items-center h-full" data-id="element-308">
              <div className="flex items-center gap-4 w-full" data-id="element-309">
                <button className="text-command-text-muted hover:text-[#e0e0e0] transition-colors" title="Attach file" data-id="element-310">
                  <PlusIcon size={22} data-id="element-311" />
                </button>
                <textarea ref={textareaRef} className="flex-grow bg-transparent focus:outline-none placeholder:text-command-text-muted text-lg py-2 resize-none h-10 overflow-hidden" placeholder="Type a new field note" value={message} onChange={e => setMessage(e.target.value)} rows={1} data-id="element-312" />
                <button className={`rounded-full w-12 h-12 flex items-center justify-center transition-all ${message.trim() ? 'bg-[#4a90e2] text-white hover:bg-[#3a7bc2]' : 'bg-white/5 text-[#888888]'}`} disabled={!message.trim()} onClick={() => {
                alert(`Saving note: ${message}`);
                setMessage('');
              }} data-id="element-313">
                  <SendIcon size={20} className={message.trim() ? 'text-white' : 'text-command-text-muted'} data-id="element-314" />
                </button>
              </div>
            </div>

            <button onClick={() => setIsVoiceMode(!isVoiceMode)} className={`rounded-full w-14 h-14 flex items-center justify-center transition-all flex-shrink-0 border ${isVoiceMode ? 'bg-[#4a90e2] border-[#4a90e2] shadow-lg shadow-[#4a90e2]/20' : 'bg-[#151A22] border-command-border hover:bg-white/5'}`} data-id="element-315">
              <MicIcon size={22} className={isVoiceMode ? 'text-white' : 'text-[#e0e0e0]'} data-id="element-316" />
            </button>
          </div>
        </div>
      </div>
    </main>;
};